import { useState } from 'react';
import { toast } from 'react-toastify';
import { useApi } from '@bumble/app/hooks/use-api';
import {
  Box,
  Grid,
  TextField,
  Divider,
  Typography,
  FormControlLabel,
  Checkbox,
  Button
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { apiError } from '@bumble/app/utils/api-error';

interface EthernetDeviceNIDInfoEditProps {
  onSubmit: () => void;
  metroId: string;
  metroName: string;
  shippingAddress: string;
  siteAddress: string;
  description: string;
  providerServiceID: string;
  clientCircuitID: string;
  localContactName: string;
  localContactEmail: string;
  wanCirMbps: string;
  wanEirMbps: string;
  wanUserDataVlanId: string;
  wanQInQVlanId: string;
  wanMgmtVlanId: string;
};

export const EthernetDeviceNIDInfoEdit = ({
  onSubmit, metroId, metroName, shippingAddress, siteAddress, description,
  providerServiceID, clientCircuitID, wanCirMbps, wanEirMbps, wanUserDataVlanId,
  wanQInQVlanId, wanMgmtVlanId, localContactName, localContactEmail
} : EthernetDeviceNIDInfoEditProps) => {
  const api = useApi();

  const [newMetroName, setNewMetroName] = useState<string>(metroName);
  const [newShippingAddress, setNewShippingAddress] = useState<string>(shippingAddress);
  const [newDescription, setNewDescription] = useState<string>(description);
  const [newSiteAddress, setNewSiteAddress] = useState<string>(siteAddress);
  const [newProviderServiceID, setNewProviderServiceID] = useState<string>(providerServiceID);
  const [newClientCircuitID, setNewClientCircuitID] = useState<string>(clientCircuitID);
  const [newLocalContactName, setNewLocalContactName] = useState<string>(localContactName);
  const [newLocalContactEmamil, setNewLocalContactEmail] = useState<string>(localContactEmail);
  const [vlan, setVlan] = useState<boolean>(wanUserDataVlanId != "");
  const [newWanUserDataVlanId, setNewWanUserDataVlanId] = useState<string>(wanUserDataVlanId);
  const [qInQVlan, setQInQVlan] = useState<boolean>(wanQInQVlanId != "");
  const [newWanQInQVlanId, setNewWanQInQVlanId] = useState<string>(wanQInQVlanId);
  const [newMgmtVlanId, setNewMgmtVlanId] = useState<string>(wanMgmtVlanId);
  const [newCirMbps, setNewCirMbps] = useState<string>(wanCirMbps);
  const [newEirMbps, setNewEirMbps] = useState<string>(wanEirMbps);

  const metroInfoUpdatePost = useMutation(api.updateMetroInfoApiV1UpdateMetroInfoPost, {
    onSuccess() {
      toast.success('Updated Metro Device NID info successfully.');
      onSubmit();
    },
    onError: apiError,
  });

  const handleSubmit = (): void => {
    metroInfoUpdatePost.mutate({
      metro_id: metroId,
      metro_name: newMetroName,
      shipping_address: newShippingAddress,
      site_address: newSiteAddress,
      description: newDescription,
      provider_service_id: newProviderServiceID,
      client_circuit_id: newClientCircuitID,
      contact_name: newLocalContactName,
      contact_email: newLocalContactEmamil,
      ...(vlan && {
        wan_user_data_vlan_id: Number(newWanUserDataVlanId)
      }),
      ...(qInQVlan && {
        wan_q_in_q_vlan_id: Number(newWanQInQVlanId)
      }),
      wan_mgmt_vlan_id: Number(newMgmtVlanId),
      wan_cir_mbps: Number(newCirMbps),
      wan_eir_mbps: Number(newEirMbps)
    });
  };

  const isDisabled = (): boolean => {
    const def = !(newMetroName) || isNaN(Number(newCirMbps)) || isNaN(Number(newEirMbps))
      || isNaN(Number(newWanUserDataVlanId));
    if (vlan)
      return isNaN(Number(newWanUserDataVlanId)) || def;
    if (qInQVlan)
      return isNaN(Number(newWanQInQVlanId)) || def;
    return def;
  };

  return (
    <Box pt={2}>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextField
            sx={{ mb: 4 }}
            fullWidth
            label="Device ID"
            variant="outlined"
            value={metroId}
            disabled
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="metro_name"
            label="Metro Name"
            variant="outlined"
            value={newMetroName}
            onChange={(e) => setNewMetroName(e.target.value)}
            autoFocus
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="shipping_address"
            label="Shipping Address"
            variant="outlined"
            value={newShippingAddress}
            onChange={(e) => setNewShippingAddress(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="site_address"
            label="Site Address"
            variant="outlined"
            value={newSiteAddress}
            onChange={(e) => setNewSiteAddress(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="description"
            label="Description"
            variant="outlined"
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="provider_service_id"
            label="Provider Service ID"
            variant="outlined"
            value={newProviderServiceID}
            onChange={(e) => setNewProviderServiceID(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="client_circuit_id"
            label="Client Circuit ID"
            variant="outlined"
            value={newClientCircuitID}
            onChange={(e) => setNewClientCircuitID(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="contact_name"
            label="Local Contact Name"
            variant="outlined"
            value={newLocalContactName}
            onChange={(e) => setNewLocalContactName(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="contact_email"
            label="Local Contact Email"
            variant="outlined"
            value={newLocalContactEmamil}
            onChange={(e) => setNewLocalContactEmail(e.target.value)}
          />
        </Grid>
      </Grid>
      <Divider sx={{ my: 4 }} />
      <Typography variant="body2" sx={{ mb: 4 }}>
        UNI (WAN) interface:
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={vlan}
                onChange={(e) => setVlan(e.target.checked)}
              />
            }
            label="Enable VLAN tagging"
          />
        </Grid>
        {vlan && <Grid item xs={12}>
          <TextField
            fullWidth
            id="wan_data_vlan_id"
            label="User Data VLAN ID"
            variant="outlined"
            value={newWanUserDataVlanId}
            onChange={(e) => setNewWanUserDataVlanId(e.target.value)}
          />
        </Grid>}
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={qInQVlan}
                onChange={(e) => setQInQVlan(e.target.checked)}
              />
            }
            label="Enable Q-in-Q VLAN Tagging"
          />
        </Grid>
        {qInQVlan && <Grid item xs={12}>
          <TextField
            fullWidth
            id="wan_q_in_q_vlan_id"
            label="Q-in-Q VLAN ID"
            variant="outlined"
            value={newWanQInQVlanId}
            onChange={(e) => setNewWanQInQVlanId(e.target.value)}
          />
        </Grid>}
      </Grid>
      <Divider sx={{ my: 4 }} />
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="wan_mgmt_vlan_id"
            label="Mgmt VLAN ID"
            variant="outlined"
            value={newMgmtVlanId}
            onChange={(e) => setNewMgmtVlanId(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="wan_cir_mbps"
            label="CIR (Committed Information Rate)"
            variant="outlined"
            value={newCirMbps}
            onChange={(e) => setNewCirMbps(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            id="wan_eir_mbps"
            label="EIR (Exceeded Information Rate)"
            variant="outlined"
            value={newEirMbps}
            onChange={(e) => setNewEirMbps(e.target.value)}
          />
        </Grid>
      </Grid>
      <Box display="flex" justifyContent="flex-end" sx={{ mt: 5 }}>
        <Button variant="outlined" onClick={handleSubmit}
          disabled={isDisabled()}>
          Save NID Info
        </Button>
      </Box>
    </Box>
  )
};
